import { useEffect, useState } from 'react'
import CookieEnableModal from 'packages/eid-ui/CookieEnableModal'
import { isChrome, isSafari, isFirefox, isEdge, isOpera } from 'react-device-detect'

const CookieChecker = ({ config, children }) => {
    const [isCookieEnabled, setIsCookieEnabled] = useState(null)
    const [isMac, setIsMac] = useState(false)

    useEffect(() => {
        checkOperatingSystem()
    }, [])

    const checkOperatingSystem = () => {
        try {
            const isIos = navigator.platform.toUpperCase().indexOf('MAC') >= 0
            setIsMac(isIos)
        } catch (err) {}
    }

    const ChromEnableStepsMac = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'ChromeBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Chrome</span> you will need to <span>change your cookies settings</span>:',
        items: [
            'Click on the <span>three vertical dots (Menu)</span> in the top-right corner of the browser window.',
            'Select <span>Settings</span> from the drop-down menu.',
            'In the Settings tab, scroll down and click on <span>Privacy and security</span> in the left-hand menu.',
            'Under the <span>Privacy and security</span> section, click on <span>Cookies and other site data.</span>',
            'Scroll down and click on <span>Third-Party Cookies</span>',
            'Select the <span>Allow Third-Party Cookies</span> option.',
            'Close and reopen Chrome to ensure the changes take effect.',
        ],
    }
    const ChromEnableSteps = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'ChromeBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Chrome</span> you will need to <span>change your cookies settings</span>:',
        items: [
            `<span>Open Chrome Browser:</span> Launch Google Chrome on your Windows computer.`,
            `<span>Access Settings:</span> Click on the three dots located at the top-right corner of the browser window to open the Chrome menu. Then, select <span>Settings</span> from the dropdown menu.`,
            `Navigate to Privacy and Security Settings: In the Settings tab, scroll down and click on <span>Privacy and security</span> from the left-hand sidebar.`,
            `Choose Site Settings: Under the <span>Privacy and security</span> section, look for <span>Site settings</span> and click on it.`,
            `Find Cookies and Site Data Settings: Scroll down to locate the <span>Cookies and site data</span> section.`,
            `Allow Third-Party Cookies: Within the <span>Cookies and site data</span> section, you'll see an option labeled <span>Block third-party cookies.</span> Click on this option to toggle it off, allowing third-party cookies.`,
            `Confirm Changes: After toggling off the option to block third-party cookies, Chrome will automatically save your settings. You may need to refresh any open web pages or restart your browser for the changes to take effect.`
        ],
    }

    const SafariEnableStepsMac = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'SafariBrowser',
        title: 'Please <span>Enable Third Party Cookies</span> to continue',
        subTitle:
            'In order to proceed with <span>Safari</span> you will need to enable 3rd-party cookies:',
        items: [
            'Launch the Safari browser on your Mac.',
            'Click on <span>Safari</span> in the menu bar at the top-left corner of your screen and choose <span>Settings</span> from the drop-down menu.',
            'In the Preferences window, click on the <span>Privacy</span> tab at the top.',
            `Under the <span>Cookies and website data</span> section, you'll see a few options:`,
            `If <span>Block all cookies</span> is selected, you'll need to change it to <span>Allow from websites I visit</span> or <span>Always allow.</span>`,
            'Check the box next to <span>Allow all cookies</span> to enable third-party cookies specifically.',
            `Once you've selected the desired cookie settings, close the Preferences window. Your changes will be automatically saved.`,
        ],
    }

    const FirefoxEnableStepsMac = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'FireFoxBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Firefox</span> you will need to <span>change your cookies settings</span>:',
        items: [
            `Launch the Firefox browser on your computer.`,
            `Click on the menu button (three horizontal lines) in the top-right corner of the Firefox window. From the menu, select <span>Preferences.</span> On Windows, this may appear as <span>Options.<span>`,
            `In the Preferences window, click on <span>Privacy & Security</span> from the left-hand sidebar.`,
            `Scroll down to the <span>Cookies and Site Data</span> section. Here, you'll find several options:`,
            `Make sure the checkbox for <span>Delete cookies and site data when Firefox is closed</span> is unchecked if you want to retain cookies.`,
            `Click on the <span>Manage Permissions...</span> button next to <span>Cookies and Site Data.</span>`,
            `In the Permissions for <span>Cookies and Site Data</span> window, uncheck the box next to <span>Block cookies and site data from third parties.</span>`,
            `After unchecking the box for blocking third-party cookies, click <span>Save Changes</span> to apply the new settings.`,
            `Close the Preferences window to finish.`
        ],
    }

    const FirefoxEnableSteps = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'FireFoxBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Firefox</span> you will need to <span>change your cookies settings</span>:',
        items: [
            `<span>Open Firefox Browser:</span> Launch Firefox on your Windows computer.`,
            `<span>Access Preferences/Settings:</span> Click on the three horizontal lines (menu icon) in the top-right corner of the browser window to open the Firefox menu. From the menu, select <span>Options.</span>`,
            `<span>Navigate to Privacy & Security Settings:</span> In the Options tab, click on <span>Privacy & Security</span> from the left-hand sidebar.`,
            `<span>Adjust Cookie Settings:</span> Scroll down to the <span>Cookies and Site Data</span> section.`,
            `<span>Allow Third-Party Cookies:</span> Within the <span>Cookies and Site Data</span> section, you'll see an option labeled <span>Accept cookies and site data from websites (recommended).</span> Ensure that this option is checked.`,
            `<span>Manage Exceptions (Optional):</span> If you want to allow or block third-party cookies for specific websites, you can click on the <span>Exceptions</span> button next to the option mentioned in the previous step. Here you can add websites and specify whether to allow or block cookies from them.`,
            `<span>Confirm Changes:</span> After adjusting your cookie settings, you can close the Options tab.`,
            `<span>Restart Firefox (if necessary):</span> In some cases, you may need to restart Firefox for the changes to take effect.`,
        ],
    }

    const OperaEnableStepMac = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'OperaBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Opera</span> you will need to <span>change your cookies settings</span>:',
        items: [
            `Launch the Opera browser on your Mac.`,
            `Click on the Opera menu in the top-left corner of the screen (next to the Apple logo). From the drop-down menu, select <span>Preferences.</span>`,
            `In the Preferences window, click on <span>Privacy & Security</span> from the left-hand sidebar.`,
            `Scroll down to the <span>Cookies</span> section and ensure that the checkbox for <span>Block third-party cookies and site data</span> is unchecked. This allows third-party cookies to be accepted.`,
            `After adjusting the cookie settings, close the Preferences window. Your changes will be saved automatically.`
        ],
    }

    const OperaEnableStep = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'OperaBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Opera</span> you will need to <span>change your cookies settings</span>:',
        items: [
            `<span>Open Opera Browser:</span> Launch Opera on your Windows computer.`,
            `<span>Access Settings:</span> Click on the Opera menu button, which is located in the top-left corner of the browser window and looks like the Opera logo (a red "O"). From the menu, select <span>Settings.</span>`,
            `<span>Navigate to Privacy & Security Settings:</span> In the Settings tab, click on <span>Privacy & security</span> from the left-hand sidebar.`,
            `<span>Manage Cookies Settings:</span> Scroll down to the <span>Privacy</span> section.`,
            `<span>Enable "Cookies":</span> Within the <span>Privacy</span> section, you'll see an option labeled <span>Cookies.</span> Ensure that this option is checked to allow cookies.`,
            `<span>Manage Exceptions (Optional):</span> If you want to allow or block cookies for specific websites, you can click on the <span>Manage exceptions</span> button next to the <span>Cookies</span> option. Here you can add websites and specify whether to allow or block cookies from them.`,
            `<span>Confirm Changes:</span> After adjusting your cookie settings, you can close the Settings tab.`,
            `<span>Restart Opera (if necessary):</span> In some cases, you may need to restart Opera for the changes to take effect.`,
        ],
    }

    const EdgeEnableSteps = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'EdgeBrowser',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle:
            'For using <span>Microsoft Edge</span> you will need to <span>change your cookies settings</span>:',
        items: [
           `<span>Open Microsoft Edge:</span> Launch Edge on your Windows computer.`,
           `<span>Access Settings:</span> Click on the three dots (ellipsis) located in the top-right corner of the browser window to open the Edge menu. From the menu, select <span>Settings.</span>`,
           `<span>Navigate to Cookies Settings:</span> In the Settings tab, click on <span>Privacy, search, and services</span> from the left-hand sidebar.`,
           `<span>Manage Cookies Settings:</span> Scroll down to the <span>Cookies and site permissions</span> section.`,
           `<span>Allow Third-Party Cookies:</span> Within the <span>Cookies and site permissions</span> section, you'll see an option labeled <span>Cookies.</span> Click on it.`,
           `Enable <span>Allow All Cookies</span>: Toggle the switch to enable the option </span>Allow sites to save and read cookie data (recommended).</span> This setting will allow all cookies, including third-party cookies.`,
           `Manage Exceptions (Optional): If you want to allow or block cookies for specific websites, you can click on the <span>Manage and delete cookies and site data</span> option. Here you can add websites and specify whether to allow or block cookies from them.`,
           `<span>Confirm Changes:</span> After adjusting your cookie settings, you can close the Settings tab.`,
           `<span>Restart Edge (if necessary):</span> In some cases, you may need to restart Edge for the changes to take effect.`,
        ],
    }

    const DefaultSteps = {
        btnLabel: 'OK, I Changed my Cookie Settings, Reload Page',
        iconName: 'ComputerDefault',
        title: 'Please <span>Change your Cookie Settings</span> to continue',
        subTitle: 'For using you will need to change your cookies settings:',
        items: [
            'At the top right, click More Settings.',
            'Under "Privacy and Security" click Cookies and other site data.',
            'Select option: Allow all cookies.',
        ],
    }

    useEffect(() => {
        window.addEventListener('message', function listen(event) {
            try {
                if (event.data === 'MM:3PCunsupported') {
                    setIsCookieEnabled(false)
                } else if (event.data === 'MM:3PCsupported') {
                    setIsCookieEnabled(true)
                }
            } catch (err) {
                setIsCookieEnabled(true)
            }
        })

        let isSameOrigin = false

        const baseUrlDomain = new URL(config.BASE_EID_URL).hostname
        const apiUrlDomain = new URL(config.API_URL).hostname

        if (baseUrlDomain === apiUrlDomain) {
            isSameOrigin = true
        }
        if (process.env.NODE_ENV === 'development') {
            // Cookies are not checked if isSameOrigin is true
            isSameOrigin = true
        }

        try {
            if (navigator && navigator.cookieEnabled === false) {
                setIsCookieEnabled(false)
            } else {
                if (document && !isSameOrigin) {
                    let frame = document.createElement('iframe')
                    frame.id = '3pc'
                    frame.src = `${config.BASE_EID_URL}/3rdpartycookiecheck/start.html`
                    frame.style.display = 'none'
                    frame.style.position = 'fixed'
                    document.body.appendChild(frame)
                } else {
                    setIsCookieEnabled(true)
                }
            }
        } catch (err) {}

        return window.removeEventListener('message', function listen(event) {
            console.log('error', event)
        })
    }, [])

    const handleAccpet = () => {
        window.location.reload()
    }

    const getModalSteps = () => {
        if (isChrome) {
            return isMac ? ChromEnableStepsMac : ChromEnableSteps
        } else if (isSafari) {
            return SafariEnableStepsMac
        } else if (isFirefox) {
            return isMac ? FirefoxEnableStepsMac : FirefoxEnableSteps
        } else if (isEdge) {
            return EdgeEnableSteps
        }else if (isOpera) {
            return isMac ? OperaEnableStepMac : OperaEnableStep
        } else {
            return DefaultSteps
        }
    }

    const bindUI = () => {
        if (isCookieEnabled === null) {
            return <></>
        } else if (isCookieEnabled) {
            return children
        } else {
            return (
                <CookieEnableModal
                    bannerData={getModalSteps()}
                    handleAccpet={() => handleAccpet()}
                />
            )
        }
    }

    return <>{bindUI()}</>
}

export default CookieChecker
